/**
 * =====================================================================
 * AUTOMATION TAB - AUTO-DEPRECIATION SETTINGS & STATUS
 * =====================================================================
 *
 * Features:
 * - Dashboard showing automation status and statistics
 * - Configuration for enabling/disabling auto-depreciation
 * - Email notification settings
 * - Run history with status indicators
 * - Manual trigger for testing
 * - Pending depreciation months view
 *
 * =====================================================================
 */

import React, { useState, useEffect } from 'react'
import { AdminUser } from '@/lib/authService'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  CheckCircle2,
  XCircle,
  AlertCircle,
  Clock,
  Calendar,
  Mail,
  Play,
  Settings as SettingsIcon,
  TrendingUp,
  FileText,
} from 'lucide-react'
import { formatCurrency } from '@/lib/currency'
import AutoDepreciationService, {
  AutoDepreciationConfig,
  DepreciationLogEntry,
  PendingDepreciationMonth,
} from '@/services/autoDepreciationService'

interface AutomationTabProps {
  user: AdminUser | null
  refreshTrigger: number
  onDataChange: () => void
}

export default function AutomationTab({ user, refreshTrigger, onDataChange }: AutomationTabProps) {
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [config, setConfig] = useState<AutoDepreciationConfig | null>(null)
  const [log, setLog] = useState<DepreciationLogEntry[]>([])
  const [pendingMonths, setPendingMonths] = useState<PendingDepreciationMonth[]>([])
  const [showSettingsDialog, setShowSettingsDialog] = useState(false)
  const [showManualRunDialog, setShowManualRunDialog] = useState(false)
  const [manualRunPeriod, setManualRunPeriod] = useState(
    new Date().toISOString().split('T')[0].slice(0, 7) // YYYY-MM format
  )

  // Form state for settings
  const [formData, setFormData] = useState({
    enabled: false,
    day: 1,
    emails: [] as string[],
    emailInput: '',
  })

  useEffect(() => {
    if (user?.churchId) {
      loadData()
    }
  }, [user, refreshTrigger])

  async function loadData() {
    if (!user?.churchId) return

    try {
      setLoading(true)
      const [configData, logData, pendingData] = await Promise.all([
        AutoDepreciationService.getSettings(user.churchId),
        AutoDepreciationService.getLog(user.churchId, 12),
        AutoDepreciationService.getPendingMonths(user.churchId),
      ])

      setConfig(configData)
      setLog(logData)
      setPendingMonths(pendingData)

      // Update form data
      if (configData) {
        setFormData({
          enabled: configData.enabled || false,
          day: configData.day || 1,
          emails: configData.notificationEmails || [],
          emailInput: '',
        })
      }
    } catch (error) {
      console.error('Error loading automation data:', error)
      // Don't show alert for initial load failures - columns might not exist yet
      // Just set empty defaults
      setConfig(null)
      setLog([])
      setPendingMonths([])
    } finally {
      setLoading(false)
    }
  }

  function handleOpenSettings() {
    setShowSettingsDialog(true)
  }

  async function handleSaveSettings() {
    if (!user?.churchId) return

    setSaving(true)
    try {
      await AutoDepreciationService.updateSettings(user.churchId, {
        enabled: formData.enabled,
        day: formData.day,
        notificationEmails: formData.emails,
      })

      alert('Success: Auto-depreciation settings updated')
      setShowSettingsDialog(false)
      onDataChange()
      loadData()
    } catch (error) {
      console.error('Error saving settings:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to save settings'}`)
    } finally {
      setSaving(false)
    }
  }

  function handleAddEmail() {
    const email = formData.emailInput.trim()
    if (email && formData.emails && !formData.emails.includes(email)) {
      // Basic email validation
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        alert('Error: Please enter a valid email address')
        return
      }
      setFormData({
        ...formData,
        emails: [...formData.emails, email],
        emailInput: '',
      })
    }
  }

  function handleRemoveEmail(email: string) {
    setFormData({
      ...formData,
      emails: formData.emails.filter(e => e !== email),
    })
  }

  function handleOpenManualRun() {
    setShowManualRunDialog(true)
  }

  async function handleManualRun() {
    if (!user?.churchId) return

    // Convert YYYY-MM to Date
    const [year, month] = manualRunPeriod.split('-').map(Number)
    const periodDate = new Date(year, month - 1, 1)

    setSaving(true)
    try {
      // TODO: Call API endpoint to trigger manual run
      // This would call the cron endpoint with the specific period
      alert(`Manual run for ${manualRunPeriod} would be triggered here.\nImplement API call to /api/cron/auto-depreciation with periodDate.`)

      setShowManualRunDialog(false)
      onDataChange()
      loadData()
    } catch (error) {
      console.error('Error running manual depreciation:', error)
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to run depreciation'}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
      </div>
    )
  }

  const isEnabled = config?.enabled || false
  const lastRun = config?.lastRun
  const lastStatus = config?.lastStatus
  const nextScheduledRun = null // TODO: Calculate from config.day

  // Calculate stats
  const totalRuns = log.length
  const successfulRuns = log.filter(l => l.status === 'success').length
  const totalAssetsProcessed = log.reduce((sum, l) => sum + l.assetsProcessed, 0)
  const totalDepreciation = log.reduce((sum, l) => sum + l.totalDepreciation, 0)

  return (
    <div className="space-y-6">
      {/* Status Banner */}
      {isEnabled ? (
        <Alert>
          <CheckCircle2 className="h-4 w-4" />
          <AlertTitle>Auto-Depreciation Enabled</AlertTitle>
          <AlertDescription>
            Depreciation will be posted automatically on day {config?.day} of each month at 2:00 AM.
            {nextScheduledRun && ` Next run: ${new Date(nextScheduledRun).toLocaleString()}`}
          </AlertDescription>
        </Alert>
      ) : (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Auto-Depreciation Disabled</AlertTitle>
          <AlertDescription>
            Automatic depreciation posting is currently disabled. Click "Configure Settings" to enable it.
          </AlertDescription>
        </Alert>
      )}

      {/* Dashboard Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Status</CardTitle>
            {isEnabled ? (
              <CheckCircle2 className="h-4 w-4 text-green-600" />
            ) : (
              <XCircle className="h-4 w-4 text-muted-foreground" />
            )}
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{isEnabled ? 'Active' : 'Inactive'}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {isEnabled ? 'Running automatically' : 'Manual posting only'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Runs</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalRuns}</div>
            <p className="text-xs text-muted-foreground mt-1">
              {successfulRuns} successful
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Assets Processed</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalAssetsProcessed}</div>
            <p className="text-xs text-muted-foreground mt-1">
              All time total
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Depreciation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatCurrency(totalDepreciation, 'GHS')}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Posted automatically
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2">
        <Button onClick={handleOpenSettings} variant="default">
          <SettingsIcon className="mr-2 h-4 w-4" />
          Configure Settings
        </Button>
        <Button onClick={handleOpenManualRun} variant="outline">
          <Play className="mr-2 h-4 w-4" />
          Manual Run
        </Button>
        <Button onClick={loadData} variant="outline">
          Refresh
        </Button>
      </div>

      {/* Pending Months */}
      {pendingMonths.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Pending Depreciation</CardTitle>
            <CardDescription>
              Months that need depreciation posting (not yet completed)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Period</TableHead>
                  <TableHead className="text-right">Assets</TableHead>
                  <TableHead className="text-right">Estimated Depreciation</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingMonths.map((month) => (
                  <TableRow key={month.periodDate}>
                    <TableCell className="font-medium">{month.periodName}</TableCell>
                    <TableCell className="text-right">{month.assetsCount}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(month.estimatedDepreciation, 'GHS')}
                    </TableCell>
                    <TableCell>
                      <Badge variant={month.isPosted ? 'default' : 'secondary'}>
                        {month.isPosted ? 'Posted' : 'Pending'}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Run History */}
      <Card>
        <CardHeader>
          <CardTitle>Run History</CardTitle>
          <CardDescription>
            Recent auto-depreciation execution history (last 12 runs)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {log.length === 0 ? (
            <p className="text-center text-muted-foreground py-8">
              No depreciation runs yet. Enable auto-depreciation to start automatic posting.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Run Date</TableHead>
                  <TableHead>Period</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Assets</TableHead>
                  <TableHead className="text-right">Depreciation</TableHead>
                  <TableHead className="text-right">Execution Time</TableHead>
                  <TableHead>Notification</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {log.map((entry) => (
                  <TableRow key={entry.id}>
                    <TableCell>{new Date(entry.runDate).toLocaleString()}</TableCell>
                    <TableCell>{new Date(entry.periodDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long' })}</TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          entry.status === 'success'
                            ? 'default'
                            : entry.status === 'partial_success'
                            ? 'secondary'
                            : 'destructive'
                        }
                      >
                        {entry.status === 'success' ? (
                          <CheckCircle2 className="mr-1 h-3 w-3" />
                        ) : entry.status === 'partial_success' ? (
                          <AlertCircle className="mr-1 h-3 w-3" />
                        ) : (
                          <XCircle className="mr-1 h-3 w-3" />
                        )}
                        {entry.status.replace('_', ' ')}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">{entry.assetsProcessed}</TableCell>
                    <TableCell className="text-right">
                      {formatCurrency(entry.totalDepreciation, 'GHS')}
                    </TableCell>
                    <TableCell className="text-right">
                      {entry.executionTimeMs ? `${entry.executionTimeMs}ms` : '-'}
                    </TableCell>
                    <TableCell>
                      {entry.notificationSent ? (
                        <Badge variant="outline">
                          <Mail className="mr-1 h-3 w-3" />
                          Sent
                        </Badge>
                      ) : (
                        <span className="text-muted-foreground text-sm">-</span>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Settings Dialog */}
      <Dialog open={showSettingsDialog} onOpenChange={setShowSettingsDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Auto-Depreciation Settings</DialogTitle>
            <DialogDescription>
              Configure automatic monthly depreciation posting
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-6">
            {/* Enable/Disable */}
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label htmlFor="enabled">Enable Auto-Depreciation</Label>
                <p className="text-sm text-muted-foreground">
                  Automatically post depreciation on a scheduled day each month
                </p>
              </div>
              <Switch
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
              />
            </div>

            {/* Day of Month */}
            <div className="space-y-2">
              <Label htmlFor="day">Day of Month (1-28)</Label>
              <Input
                id="day"
                type="number"
                min={1}
                max={28}
                value={formData.day}
                onChange={(e) => setFormData({ ...formData, day: parseInt(e.target.value) || 1 })}
                disabled={!formData.enabled}
              />
              <p className="text-xs text-muted-foreground">
                Depreciation will be posted on this day of each month at 2:00 AM.
                Limited to 1-28 to ensure the day exists in all months.
              </p>
            </div>

            {/* Email Notifications */}
            <div className="space-y-2">
              <Label>Email Notifications</Label>
              <p className="text-sm text-muted-foreground mb-2">
                Email addresses to notify when depreciation is posted or if errors occur
              </p>

              {/* Email List */}
              {formData.emails && formData.emails.length > 0 && (
                <div className="space-y-2 mb-2">
                  {formData.emails.map((email) => (
                    <div key={email} className="flex items-center justify-between p-2 bg-muted rounded">
                      <span className="text-sm">{email}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveEmail(email)}
                        disabled={!formData.enabled}
                      >
                        Remove
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Add Email Input */}
              <div className="flex gap-2">
                <Input
                  placeholder="email@example.com"
                  value={formData.emailInput}
                  onChange={(e) => setFormData({ ...formData, emailInput: e.target.value })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      handleAddEmail()
                    }
                  }}
                  disabled={!formData.enabled}
                />
                <Button onClick={handleAddEmail} variant="outline" disabled={!formData.enabled}>
                  Add
                </Button>
              </div>
            </div>

            {/* Info Alert */}
            <Alert>
              <Calendar className="h-4 w-4" />
              <AlertTitle>How It Works</AlertTitle>
              <AlertDescription className="text-sm space-y-1">
                <p>• A scheduled job runs at 2:00 AM on the day you specify</p>
                <p>• It calculates and posts depreciation for all active assets</p>
                <p>• Journal entries are created automatically in the general ledger</p>
                <p>• Email notifications are sent with success or error details</p>
                <p>• You can monitor all runs in the history table below</p>
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSettingsDialog(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleSaveSettings} disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Run Dialog */}
      <Dialog open={showManualRunDialog} onOpenChange={setShowManualRunDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Manual Depreciation Run</DialogTitle>
            <DialogDescription>
              Trigger depreciation posting for a specific month
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="manualPeriod">Select Month</Label>
              <Input
                id="manualPeriod"
                type="month"
                value={manualRunPeriod}
                onChange={(e) => setManualRunPeriod(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">
                This will calculate and post depreciation for all active assets in the selected month
              </p>
            </div>

            <Alert variant="default">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>
                Make sure the depreciation for this month hasn't already been posted.
                Check the run history and pending months above.
              </AlertDescription>
            </Alert>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowManualRunDialog(false)} disabled={saving}>
              Cancel
            </Button>
            <Button onClick={handleManualRun} disabled={saving}>
              {saving ? 'Running...' : 'Run Depreciation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
